import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setUpdateUsersList } from 'reducers/misc';
import { UsersApi } from '../../api/usersApi';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { DropzoneCSVOnly } from "../../helpers/FileUploadPanel/DropzoneCSVOnly";
import { Spinner } from 'components/spinner/Spinner';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const BulkUpload: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
}) => {
  const [error, setError] = useState<string>('');
  const [errorList, setErrorList] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [fileUploaded, setFileUploaded] = useState<any>(null); 

  const dispatch = useDispatch();
  const setUpdateList = (status: boolean) =>
    dispatch(setUpdateUsersList(status));

  const downloadTemplate = async () => {
    setIsSubmitting(true);

    const result = await UsersApi.downloadUserTemplateCSV();
    
    if (result.data) {
      const downloadUrl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'Manage Users Template.csv');
      document.body.appendChild(link);
  
      link.click();
      setTimeout(function () {
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      togglePanelOpen(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setErrorList(null);
      setIsSubmitting(false);
    }
  };  

  const downloadList = async () => {
    setIsSubmitting(true);

    const result = await UsersApi.downloadUserListCSV();
    
    if (result.data) {
      const downloadUrl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'Manage Users List.csv');
      document.body.appendChild(link);
  
      link.click();
      setTimeout(function () {
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      togglePanelOpen(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setErrorList(null);
      setIsSubmitting(false);
    }
  };

  const uploadList = async () => {
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("CsvFile", fileUploaded[0]);

    const result = await UsersApi.uploadUserListCSV(formData);

    if (result.data) {
      setUpdateList(true);
      togglePanelOpen(false);
    }

    if(result.insertionsStatusErr){
      setError('');
      setErrorList(result.insertionsStatusErr);
      setIsSubmitting(false);
    }

    if (result.error) {
      const error = result.error.message;
      setError(error);
      setErrorList(null);
      setIsSubmitting(false);
    }
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={"Bulk Edit / Bulk Upload"}
      position="right"
      givenClass="BulkUploadUsers"
      noFooter={true}
      handleClose={() => togglePanelOpen(false)}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={isSubmitting}
    >
      {error && <p className="GeneralError2">{error}</p>}

      {errorList && 
        <>
          <p className="GeneralError2" style={{ marginBottom: "0px" }}><strong>Spreadsheet errors:</strong></p>
          <>
            {
              errorList.map((item: any, index: number) => {
                return (<p key={index} className="GeneralError2" style={{ marginBottom: "0px" }}>{item}</p>);
              })
            }
          </>
        </>
      }

      <div className="FileDLTxtBtn">
        <div className="TxtBlock">
          <p>Use this file to bulk edit your school email addresses. Save as a .csv file.</p>
        </div>
        <div className="BtnBlock">
          <div 
            className="Btn BtnOrange"
            onClick={() => downloadList()}
          >
            <span>Bulk Edit</span>
          </div>
        </div>
      </div>

      <div className="FileDLTxtBtn">
        <div className="TxtBlock">
          <p>Use this file to bulk upload staff. Save as a .csv file.</p>
        </div>
        <div className="BtnBlock">
          <div 
            className="Btn BtnOrange"
            onClick={() => downloadTemplate()}
          >
            <span>Bulk Upload</span>
          </div>
        </div>
      </div>

      <div className="FileDLTxtBtn FullWidthRows">
        <div className="TxtBlock">
          <p>Click here to upload your csv file.</p>
        </div>
        <div className="BtnBlock">
          <div className="StyledFileUpload">
            <DropzoneCSVOnly
              uploadedFile={fileUploaded}
              onDrop={file => {
                if (file && isCSV(file[0])) {
                  setFileUploaded(file);
                  setError("");
                } else {
                  setFileUploaded(null);
                  setError("Please upload only csv files.");
                }            
              }}
              resetFile={() => {
                setFileUploaded(null);
                setError("Please upload only csv files.");
              }}
              readOnly={false}
              hasUploadedImage={fileUploaded ? true : false}
              fileName={fileUploaded ? fileUploaded[0].name : ""}
              isCSVCheck={isCSV}
            />           

            {fileUploaded &&
              <>
                <div 
                  className="Btn BtnLightOrange"
                  onClick={() => {
                    setError('');
                    setErrorList(null);                  
                    setFileUploaded(null);
                  }}
                >
                  <span>Reset</span>
                </div>

                <div className="LineSpacer3"></div>
              </>            
            }

            <div 
              className={"Btn BtnOrange" + (fileUploaded ? "" : " Disable")}
              onClick={() => uploadList()}
            >
              <span>Upload File</span>
            </div>                                     
          </div>
        </div>
      </div>

      {isSubmitting && <Spinner /> }          
    </PopupOrPanel>
  );
};

const isCSV = (file: File) => {
  if (
    file.type === "text/csv" ||
    file.type === "application/vnd.ms-excel"
  ) {
    return true;
  } else {
    return false;
  }
};
